import { useParams } from "react-router-dom";
import { useFetcher } from "~/hooks/useFetcher";

const { VITE_CATALOG_API } = import.meta.env;

/**
 * Page size requested from the schema endpoints. The destination dropdown keeps
 * client-side search over a single page of this size.
 *
 * If ever a dataset has > 100 schemas, or we have > 100 global schemas, this limit
 * will cause some schemas to be hidden.
 */
export const SCHEMA_PAGE_SIZE = 100;

export interface DestinationSchema {
  name: string;
  description: string;
}

interface SchemaListResponse {
  items: DestinationSchema[];
}

interface UseDestinationSchemasResult {
  schemas: DestinationSchema[];
  isLoading: boolean;
}

/**
 * Schemas offered as ingest destinations for the current dataset.
 *
 * Prefers the dataset's own associated schemas (GET /v1/datasets/{dataset_id}/schemas).
 * When the dataset has none, falls back to the global schema list (GET /v1/schemas).
 * The fallback fires only on a successful empty result — a failed dataset request
 * surfaces as an empty list rather than silently falling through to global schemas.
 */
const useDestinationSchemas = (): UseDestinationSchemasResult => {
  const { datasetId } = useParams();

  const datasetSchemas = useFetcher<SchemaListResponse>(
    datasetId
      ? `${VITE_CATALOG_API}/v1/datasets/${datasetId}/schemas?limit=${SCHEMA_PAGE_SIZE}`
      : null
  );

  const hasDatasetSchemas = (datasetSchemas.data?.items.length ?? 0) > 0;
  // Only fall back to global schemas once the dataset request has resolved with zero items.
  const shouldFetchGlobal = Boolean(datasetSchemas.data) && !hasDatasetSchemas;

  const globalSchemas = useFetcher<SchemaListResponse>(
    shouldFetchGlobal ? `${VITE_CATALOG_API}/v1/schemas?limit=${SCHEMA_PAGE_SIZE}` : null
  );

  const schemas = hasDatasetSchemas
    ? (datasetSchemas.data?.items ?? [])
    : (globalSchemas.data?.items ?? []);

  return {
    schemas,
    isLoading: datasetSchemas.isLoading || (shouldFetchGlobal && globalSchemas.isLoading),
  };
};

export default useDestinationSchemas;
